'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { AiProviderIcon } from './icons';
import {
  askAiLaunchUrl,
  copyText,
  type AskAiProvider,
} from './providers';

export function AskAiSection({
  productName,
  prompt,
  providers,
  description,
  overviewHref,
  overviewLabel,
  source = 'homepage',
  onTrack,
}: {
  productName: string;
  prompt: string;
  providers: AskAiProvider[];
  description?: string;
  overviewHref?: string;
  overviewLabel?: string;
  source?: string;
  onTrack?: (event: string, props: Record<string, string>) => void;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const headingId = useId();
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        onTrack?.('ask_ai_section_viewed', { section: source, product: productName });
        observer.disconnect();
      },
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onTrack, productName, source]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  async function handleClick(provider: AskAiProvider) {
    onTrack?.('ask_ai_clicked', {
      provider: provider.id,
      section: source,
      product: productName,
    });

    if (!provider.supportsPrefill) {
      const copied = await copyText(prompt);
      if (copied) {
        setToast(`Prompt copied — paste it into ${provider.name}.`);
      }
    }

    window.open(askAiLaunchUrl(provider, prompt), '_blank', 'noopener,noreferrer');
  }

  return (
    <section
      ref={sectionRef}
      id="ask-ai"
      aria-labelledby={headingId}
      className="border-t border-slate-800"
    >
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            id={headingId}
            className="text-3xl font-bold text-white sm:text-4xl"
          >
            Ask AI about {productName}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-slate-400">
            {description ||
              `Explore ${productName}’s features, pricing and use cases using your preferred AI assistant.`}
          </p>
        </div>

        <ul className="mt-8 flex flex-wrap items-start justify-center gap-3 sm:mt-10 sm:gap-4">
          {providers.map((provider) => (
            <li key={provider.id}>
              <button
                type="button"
                title={provider.tooltip}
                aria-label={provider.ariaLabel}
                onClick={() => void handleClick(provider)}
                className="group flex w-[4.75rem] flex-col items-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 px-2 py-3 text-slate-200 transition-all hover:border-teal-500/40 hover:bg-slate-900/70 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 active:scale-[0.97] active:border-teal-500/60 sm:w-[5.75rem] sm:px-3 sm:py-3.5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700/80 bg-slate-950/70 text-slate-100 shadow-inner transition-colors group-hover:border-teal-500/30 group-hover:text-white sm:h-12 sm:w-12">
                  <AiProviderIcon id={provider.id} />
                </span>
                <span className="text-[11px] font-medium leading-tight text-slate-400 transition-colors group-hover:text-slate-200 sm:text-xs">
                  {provider.name}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <p className="mx-auto mt-6 max-w-xl text-center text-xs leading-relaxed text-slate-500">
          These buttons open third-party AI assistants. {productName} is not
          partnered with or endorsed by OpenAI, Perplexity, xAI, Anthropic or
          Google.
          {overviewHref ? (
            <>
              {' '}
              <OverviewLink href={overviewHref}>
                {overviewLabel || 'Read the AI-readable overview'}
              </OverviewLink>
              .
            </>
          ) : null}
        </p>
      </div>

      {toast ? (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-slate-100 shadow-lg shadow-black/40"
        >
          {toast}
        </div>
      ) : null}
    </section>
  );
}

function OverviewLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  const className =
    'text-teal-400/90 underline-offset-2 hover:text-teal-300 hover:underline';
  const isInternal = href.startsWith('/');
  return (
    <a
      href={href}
      className={className}
      {...(isInternal
        ? {}
        : { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </a>
  );
}
